import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * `<ActiveAuthorizationsView />` — patient-facing list of active
 * Patient-Directed Use Authority Consent Artifacts with one-click
 * revoke per ADR-027 §3 patient-side trust cues.
 *
 * Per ADR-027 §3:
 *
 *   "PHG surfaces all active third-party authorizations to the
 *    patient with trust cues that derive from registration discipline
 *    (registered app identity, registered purpose, retention
 *    discipline, current admission status). Patients can review and
 *    revoke at any time; revocation is one click away."
 *
 * Per ADR-027 §1 step 8, revocation immediately invalidates the
 * refresh token and propagates to the Authorization Framework so
 * subsequent API calls under the token are rejected. The view
 * surfaces the revoke action; the orchestrator-side flow makes the
 * revocation call to `IConsentArtifactClient.revokeConsentArtifact`.
 *
 * **Doctrinal posture.** Revocation is non-confirmatory at v1 — one
 * click revokes. PFO §3.5: "Revocation is operationally final —
 * revoked Consent Artifacts cannot be reactivated; new consent for
 * previously-consented operation requires new Consent Artifact
 * issuance." The UI surfaces this honesty: revoking is irreversible;
 * to re-grant, the patient initiates a new consent flow with the
 * app.
 */
import * as React from 'react';
import { AuthorityIndicator } from './_components/authority-indicator.js';
export function ActiveAuthorizationsView(props) {
    const [state, setState] = React.useState({ kind: 'loading' });
    const [reloadCounter, setReloadCounter] = React.useState(0);
    React.useEffect(() => {
        let cancelled = false;
        setState({ kind: 'loading' });
        props.consentArtifact
            .listActiveAppArtifacts(props.subjectPci)
            .then((artifacts) => {
            if (!cancelled) {
                setState({ kind: 'loaded', artifacts });
            }
        })
            .catch((e) => {
            if (!cancelled) {
                setState({
                    kind: 'error',
                    message: e instanceof Error ? e.message : String(e),
                });
            }
        });
        return () => {
            cancelled = true;
        };
    }, [props.consentArtifact, props.subjectPci, reloadCounter]);
    const revoke = async (artifactId) => {
        try {
            await props.consentArtifact.revokeConsentArtifact({
                artifactId,
                reason: 'patient-revoked',
            });
            setReloadCounter((n) => n + 1);
        }
        catch (e) {
            setState({
                kind: 'error',
                message: e instanceof Error ? e.message : String(e),
            });
        }
    };
    return (_jsxs("section", { "aria-labelledby": "active-auth-heading", children: [_jsx("h2", { id: "active-auth-heading", children: "Active app authorizations" }), _jsx("p", { children: "Apps you have granted access to your Smart Health Account. Revocation is one click and is immediate at the substrate. Once revoked, the grant cannot be reactivated; to re-grant, you would start a new consent flow with the app." }), renderBody(state, revoke)] }));
}
function renderBody(state, revoke) {
    switch (state.kind) {
        case 'loading':
            return (_jsx("p", { role: "status", "aria-busy": "true", children: "Loading active authorizations..." }));
        case 'error':
            return (_jsxs("p", { role: "alert", children: ["Could not load authorizations: ", _jsx("small", { children: state.message })] }));
        case 'loaded':
            if (state.artifacts.length === 0) {
                return _jsx("p", { children: "You have no active third-party app authorizations." });
            }
            return (_jsx("ul", { "aria-label": "Active app authorizations", children: state.artifacts.map((a) => (_jsxs("li", { children: [_jsxs("h3", { children: [a.appIdentity ?? '(unknown app)', ' ', _jsx(AuthorityIndicator, { state: "granted", label: "ACTIVE" })] }), a.statedPurpose !== undefined ? (_jsxs("p", { children: [_jsx("strong", { children: "Stated purpose:" }), " ", a.statedPurpose] })) : null, _jsxs("dl", { children: [_jsx("dt", { children: "Data scope" }), _jsx("dd", { children: a.dataScope.join(', ') }), _jsx("dt", { children: "Operations" }), _jsx("dd", { children: a.operationalScope.join(', ') }), _jsx("dt", { children: "Retention" }), _jsx("dd", { children: a.retentionDiscipline }), _jsx("dt", { children: "Issued" }), _jsx("dd", { children: a.issuedAt }), _jsx("dt", { children: "Artifact reference" }), _jsx("dd", { children: _jsx("code", { children: a.artifactId }) })] }), _jsx("button", { type: "button", onClick: () => {
                                void revoke(a.artifactId);
                            }, "aria-label": `Revoke access for ${a.appIdentity ?? a.artifactId}`, children: "Revoke" })] }, a.artifactId))) }));
    }
}
//# sourceMappingURL=active-authorizations-view.js.map